<template>
  <teleport to="body">
    <transition name="custom-modal-fade">
      <div
        v-if="rendered"
        ref="overlayRef"
        class="custom-modal-overlay"
        :style="overlayStyle"
        @click="onMaskClick"
      >
        <div class="custom-modal-wrapper" :class="{ 'is-mobile': isMobile }">
          <div
            ref="dialogRef"
            class="custom-modal"
            :class="modalClass"
            role="dialog"
            :aria-modal="true"
            :aria-labelledby="title ? titleId : undefined"
            tabindex="-1"
            :style="dialogStyle"
            @click.stop
          >
            <header class="custom-modal__header">
              <div class="custom-modal__title" :id="titleId">{{ title }}</div>
              <button 
                v-if="closable" 
                type="button" 
                class="custom-modal__close" 
                @click="onCancel"
              >
                <span aria-hidden="true">×</span>
              </button>
            </header>

            <section class="custom-modal__body" :style="bodyStyle">
              <slot />
            </section>

            <footer v-if="shouldShowFooter" class="custom-modal__footer">
              <slot name="footer">
                <button
                  type="button"
                  class="custom-modal__btn custom-modal__btn--ghost"
                  :disabled="cancelButtonProps?.disabled"
                  @click="onCancel"
                >
                  {{ cancelText }}
                </button>
                <button
                  type="button"
                  class="custom-modal__btn custom-modal__btn--primary"
                  :disabled="okButtonProps?.disabled || confirmLoading"
                  @click="onOk"
                >
                  <span v-if="confirmLoading" class="custom-modal__spinner" aria-hidden="true"></span>
                  {{ okText }}
                </button>
              </slot>
            </footer>

            <div
              v-if="resizable"
              class="custom-modal__resize"
              role="presentation"
              @pointerdown.prevent="startResizing"
            />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed } from 'vue'
import useModalController from '@/composables/useModalController'

const props = defineProps({
  open: { type: Boolean, default: false },
  modelValue: { type: Boolean, default: undefined },
  visible: { type: Boolean, default: undefined },
  title: { type: String, default: '' },
  width: { type: [String, Number], default: '720px' },
  footer: { type: [Array, Object, Function, null], default: undefined },
  bodyStyle: { type: Object, default: () => ({}) },
  maskClosable: { type: Boolean, default: true },
  closable: { type: Boolean, default: true },
  destroyOnClose: { type: Boolean, default: false },
  confirmLoading: { type: Boolean, default: false },
  okText: { type: String, default: '確定' },
  cancelText: { type: String, default: '取消' },
  okButtonProps: { type: Object, default: () => ({}) },
  cancelButtonProps: { type: Object, default: () => ({}) },
  trapFocus: { type: Boolean, default: true },
  lockScroll: { type: Boolean, default: true },
  escClosable: { type: Boolean, default: true },
  resizable: { type: Boolean, default: true },
  // 彈窗本體額外的 class，讓呼叫端依狀態調整外觀，不影響預設樣式
  modalClass: { type: [String, Array, Object], default: '' }
})

const emit = defineEmits(['update:open', 'update:visible', 'update:modelValue', 'ok', 'cancel'])

const {
  overlayRef,
  dialogRef,
  rendered,
  overlayStyle,
  dialogStyle,
  shouldShowFooter,
  isMobile,
  onMaskClick,
  onOk,
  onCancel,
  startResizing
} = useModalController(props, emit)

const titleId = computed(() => `custom-modal-title-${Math.random().toString(36).slice(2, 8)}`)
</script>

<style scoped>
.custom-modal-fade-enter-active,
.custom-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.custom-modal-fade-enter-from,
.custom-modal-fade-leave-to {
  opacity: 0;
}

.custom-modal-fade-enter-to {
  opacity: 1;
}

/* 確保 Modal 內容區域不限制下拉選單的顯示 */
:deep(.custom-modal__body) {
  overflow: visible;
}

.custom-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  backdrop-filter: blur(2px);
}

.custom-modal-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.custom-modal {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
  min-width: 320px;
  max-width: 96vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modal-zoom 0.2s ease;
}

@keyframes modal-zoom {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.custom-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #e8e8e8;
  cursor: default;
  user-select: none;
}

.custom-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
  line-height: 1.4;
}

.custom-modal__close {
  border: 1px solid #d9d9d9;
  background: #f5f5f5;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #2f2f2f;
  transition: all 0.2s ease;
}

.custom-modal__close:hover {
  background: #e6f4ff;
  border-color: #91caff;
  color: #1677ff;
}

.custom-modal__close:active {
  background: #bae0ff;
  border-color: #69b1ff;
}

.custom-modal__body {
  padding: 18px;
  overflow-y: auto;
  flex: 1;
}

.custom-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 18px 16px;
  border-top: 1px solid #e8e8e8;
}

.custom-modal__btn {
  min-width: 88px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid transparent;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.custom-modal__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.custom-modal__btn--ghost {
  background: #f7f7f7;
  border-color: #d9d9d9;
  color: #444;
}

.custom-modal__btn--ghost:hover:not(:disabled) {
  background: #ededed;
  border-color: #c7c7c7;
}

.custom-modal__btn--primary {
  background: #1677ff;
  border-color: #1677ff;
  color: #fff;
}

.custom-modal__btn--primary:hover:not(:disabled) {
  background: #3c8bff;
  border-color: #3c8bff;
}

.custom-modal__btn--primary:active:not(:disabled) {
  background: #0f6ae6;
  border-color: #0f6ae6;
}

.custom-modal__resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  background: linear-gradient(135deg, transparent 50%, #d9d9d9 50%);
}

.custom-modal__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.custom-modal:focus-visible {
  outline: 2px solid #91caff;
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .custom-modal-wrapper {
    align-items: flex-end;
  }
  .custom-modal {
    width: 100% !important;
    max-height: 88vh;
    border-radius: 12px 12px 0 0;
  }
  .custom-modal__header {
    cursor: default;
  }
}
</style>


